<script setup lang="ts">
    import { onMounted, ref, computed } from 'vue';
    import { http } from '@/shared/httpWrapper';
    import { ClientApplicationDetailsResponse, getClientApplicationDetails } from '@/shared/helpers/api';
    import { shortDate } from '@/formatters';
    import { getSqlEditorLinkBySavedQuery } from '@/shared/helpers/routing';
    import UsageStatsBackLink from './UsageStatsBackLink.vue';
    import { Chart, ArcElement, Tooltip, Colors, PieController, BarController, BarElement, CategoryScale, LinearScale } from 'chart.js';
    import { Pie as PieChart, Bar as BarChart } from 'vue-chartjs';

    Chart.register(PieController, ArcElement, Tooltip, Colors, BarController, BarElement, CategoryScale, LinearScale);

    const props = defineProps<{
        userId: string;
    }>();

    const details = ref<ClientApplicationDetailsResponse | null>(null);
    const loading = ref(false);

    async function load(): Promise<void> {
        try {
            loading.value = true;
            details.value = await getClientApplicationDetails(http, props.userId);
        } finally {
            loading.value = false;
        }
    }

    onMounted(load);

    const resourcesChartData = computed(() => ({
        labels: details.value?.topResources?.map(r => r.name) ?? [],
        datasets: [{
            data: details.value?.topResources?.map(r => r.totalCount) ?? []
        }]
    }));

    const resourcesChartOptions = {
        plugins: {
            legend: { display: false },
            tooltip: { enabled: true }
        }
    };

    const dailyUsageChartData = computed(() => ({
        labels: details.value?.dailyUsage?.map(d => `${d.month}-${d.day}`) ?? [],
        datasets: [{
            label: 'Requests',
            data: details.value?.dailyUsage?.map(d => d.totalCount) ?? []
        }]
    }));

    const dailyUsageChartOptions = {
        aspectRatio: 4,
        plugins: {
            legend: { display: false },
            tooltip: { enabled: true }
        },
        scales: {
            y: { beginAtZero: true }
        }
    };

    const monthlyUsageChartData = computed(() => ({
        labels: details.value?.monthlyUsage?.map(d => `${d.year}-${String(d.month).padStart(2, '0')}`) ?? [],
        datasets: [{
            label: 'Requests',
            data: details.value?.monthlyUsage?.map(d => d.totalCount) ?? []
        }]
    }));

    const monthlyUsageChartOptions = {
        aspectRatio: 4,
        plugins: {
            legend: { display: false },
            tooltip: { enabled: true }
        },
        scales: {
            y: { beginAtZero: true }
        }
    };

    const recentUsageChartData = computed(() => ({
        labels: details.value?.recentUsage?.map(d => d.time.slice(11, 13) + ':00') ?? [],
        datasets: [{
            label: 'Requests',
            data: details.value?.recentUsage?.map(d => d.totalCount) ?? []
        }]
    }));

    const recentUsageChartOptions = {
        aspectRatio: 4,
        plugins: {
            legend: { display: false },
            tooltip: { enabled: true }
        },
        scales: {
            y: { beginAtZero: true }
        }
    };
</script>

<template>
    <div>
        <h1 class="title">
            Client Application: {{ details?.application?.description || props.userId }}
        </h1>
        <p class="subtitle mb-1">
            User ID: {{ props.userId }}
        </p>
        <usage-stats-back-link />
        <hr>
        <b-loading
            v-model="loading"
            :is-full-page="false"
            :can-cancel="false" />

        <div
            v-if="details?.application"
            class="columns">
            <div class="column">
                <div class="block">
                    <table class="table is-narrow is-fullwidth">
                        <tbody>
                            <tr>
                                <th>Object ID</th>
                                <td>{{ details.application.objectId }}</td>
                            </tr>
                            <tr>
                                <th>Application ID</th>
                                <td>{{ details.application.applicationId }}</td>
                            </tr>
                            <tr>
                                <th>Homepage</th>
                                <td>
                                    <a
                                        v-if="details.application.homepage"
                                        :href="details.application.homepage"
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        {{ details.application.homepage }}
                                    </a>
                                </td>
                            </tr>
                            <tr>
                                <th>Contact 1</th>
                                <td>
                                    {{ details.application.contact1 }}
                                    <span v-if="details.application.contact1Mail">
                                        (&lt;<a :href="`mailto:${details.application.contact1Mail}`">{{ details.application.contact1Mail }}</a>&gt;)
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th>Contact 2</th>
                                <td>
                                    {{ details.application.contact2 }}
                                    <span v-if="details.application.contact2Mail">
                                        (&lt;<a :href="`mailto:${details.application.contact2Mail}`">{{ details.application.contact2Mail }}</a>&gt;)
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th>Remarks</th>
                                <td>{{ details.application.remarks }}</td>
                            </tr>
                            <tr>
                                <th>Date created</th>
                                <td>{{ details.application.dateCreated ? shortDate(details.application.dateCreated) : '' }}</td>
                            </tr>
                            <tr>
                                <th>Date updated</th>
                                <td>{{ details.application.dateUpdated ? shortDate(details.application.dateUpdated) : '' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <hr>

                <div v-if="details?.topEndpoints?.length">
                    <h2 class="title is-5">
                        Top endpoints (last 30 days)
                    </h2>
                    <b-table
                        :data="details.topEndpoints"
                        :narrowed="true"
                        :striped="true"
                        :hoverable="true">
                        <b-table-column
                            v-slot="tableProps"
                            field="endpoint"
                            label="Endpoint">
                            {{ tableProps.row.endpoint }}
                        </b-table-column>
                        <b-table-column
                            v-slot="tableProps"
                            field="totalCount"
                            label="Requests"
                            numeric>
                            {{ tableProps.row.totalCount }}
                        </b-table-column>
                        <b-table-column
                            v-slot="tableProps"
                            field="failureCount"
                            label="Failures"
                            numeric>
                            {{ tableProps.row.failureCount }}
                        </b-table-column>
                    </b-table>
                </div>

                <hr v-if="details?.savedQueries?.length">

                <div v-if="details?.savedQueries?.length">
                    <h2 class="title is-5">
                        Saved queries recently used (last 30 days)
                    </h2>
                    <b-table
                        :data="details.savedQueries"
                        :narrowed="true"
                        :striped="true"
                        :hoverable="true">
                        <b-table-column
                            v-slot="tableProps"
                            field="name"
                            label="Name">
                            <router-link :to="getSqlEditorLinkBySavedQuery(tableProps.row.guid)">
                                {{ tableProps.row.name }}
                            </router-link>
                        </b-table-column>
                        <b-table-column
                            v-slot="tableProps"
                            field="totalCount"
                            label="Requests"
                            numeric>
                            {{ tableProps.row.totalCount }}
                        </b-table-column>
                    </b-table>
                </div>
            </div>
            <div class="column">
                <div v-if="details?.topResources?.length">
                    <h2 class="title is-5">
                        Top resources (last 30 days)
                    </h2>
                    <div style="display: flex; justify-content: center;">
                        <pie-chart
                            :data="resourcesChartData"
                            :options="resourcesChartOptions"
                            style="max-height: 110px; max-width: 110px; margin-bottom: 1.5rem;" />
                    </div>
                    <b-table
                        :data="details.topResources"
                        :narrowed="true"
                        :striped="true"
                        :hoverable="true">
                        <b-table-column
                            v-slot="tableProps"
                            field="name"
                            label="Resource"
                            sortable>
                            {{ tableProps.row.name }}
                        </b-table-column>
                        <b-table-column
                            v-slot="tableProps"
                            field="totalCount"
                            label="Requests"
                            numeric
                            sortable>
                            {{ tableProps.row.totalCount }}
                        </b-table-column>
                        <b-table-column
                            v-slot="tableProps"
                            field="failureCount"
                            label="Failures"
                            numeric
                            sortable>
                            {{ tableProps.row.failureCount }}
                        </b-table-column>
                    </b-table>
                </div>
            </div>
            <div class="column">
                <div v-if="details?.recentUsage?.length">
                    <h2 class="title is-5">
                        Requests last 24 hours (1 hour intervals)
                    </h2>
                    <bar-chart
                        :data="recentUsageChartData"
                        :options="recentUsageChartOptions" />
                </div>
                <hr v-if="details?.recentUsage?.length && details?.dailyUsage?.length">
                <div v-if="details?.dailyUsage?.length">
                    <h2 class="title is-5">
                        Daily usage (last 30 days)
                    </h2>
                    <bar-chart
                        :data="dailyUsageChartData"
                        :options="dailyUsageChartOptions" />
                </div>
                <hr v-if="details?.dailyUsage?.length && details?.monthlyUsage?.length">
                <div v-if="details?.monthlyUsage?.length">
                    <h2 class="title is-5">
                        Monthly usage (last 12 months)
                    </h2>
                    <bar-chart
                        :data="monthlyUsageChartData"
                        :options="monthlyUsageChartOptions" />
                </div>
            </div>
        </div>
    </div>
</template>
